import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ChevronRightIcon, ChevronLeftIcon, UserIcon, BriefcaseIcon, TargetIcon, ClockIcon, PaletteIcon, CheckCircleIcon, SparklesIcon, ZapIcon, LayersIcon, TrendingUpIcon } from 'lucide-react';
import { useOnboarding, ExperienceLevel, LearningGoal, LearningStyle, TimeCommitment, Interest } from '../hooks/useOnboarding';
interface OnboardingFlowProps {
  isOpen: boolean;
  onClose: () => void;
  hook: ReturnType<typeof useOnboarding>;
}
export function OnboardingFlow({
  isOpen,
  onClose,
  hook
}: OnboardingFlowProps) {
  const {
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    data,
    updateData,
    toggleInterest,
    getRecommendation
  } = hook;
  if (!isOpen) return null;
  const progress = (currentStep + 1) / totalSteps * 100;
  const recommendation = getRecommendation();
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <div className="space-y-6" data-id="element-355">
            <div className="text-center mb-8" data-id="element-356">
              <h2 className="text-3xl font-bold text-white mb-2" data-id="element-357">
                Welcome to LeaDe
              </h2>
              <p className="text-gray-400" data-id="element-358">
                Let's personalize your learning journey. First, what should we
                call you?
              </p>
            </div>
            <div className="space-y-4" data-id="element-359">
              <div data-id="element-360">
                <label className="block text-sm font-medium text-gray-400 mb-1" data-id="element-361">
                  Your Name
                </label>
                <input type="text" value={data.name} onChange={e => updateData({
                name: e.target.value
              })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all" placeholder="e.g. Alex Johnson" autoFocus data-id="element-362" />
              </div>
              <div data-id="element-363">
                <label className="block text-sm font-medium text-gray-400 mb-1" data-id="element-364">
                  Email Address
                </label>
                <input type="email" value={data.email} onChange={e => updateData({
                email: e.target.value
              })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-brand-yellow focus:ring-1 focus:ring-brand-yellow transition-all" placeholder="name@example.com" data-id="element-365" />
              </div>
            </div>
          </div>;
      case 1:
        return <div className="space-y-6" data-id="element-366">
            <div className="text-center mb-8" data-id="element-367">
              <h2 className="text-3xl font-bold text-white mb-2" data-id="element-368">
                Experience Level
              </h2>
              <p className="text-gray-400" data-id="element-369">
                Where are you currently in your design journey?
              </p>
            </div>
            <div className="grid gap-4" data-id="element-370">
              {[{
              id: 'beginner',
              label: 'Complete Beginner',
              desc: 'No prior design experience',
              icon: UserIcon
            }, {
              id: 'intermediate',
              label: 'Junior Designer',
              desc: '1-2 years of experience',
              icon: LayersIcon
            }, {
              id: 'advanced',
              label: 'Experienced',
              desc: '3+ years looking to level up',
              icon: ZapIcon
            }].map(option => <button key={option.id} onClick={() => {
              updateData({
                experienceLevel: option.id as ExperienceLevel
              });
              nextStep();
            }} className={`flex items-center p-4 rounded-xl border transition-all text-left group ${data.experienceLevel === option.id ? 'bg-brand-yellow/10 border-brand-yellow' : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'}`} data-id="element-371">
                  <div className={`p-3 rounded-lg mr-4 ${data.experienceLevel === option.id ? 'bg-brand-yellow text-dark-900' : 'bg-white/10 text-gray-400 group-hover:text-white'}`} data-id="element-372">
                    <option.icon className="w-6 h-6" data-id="element-373" />
                  </div>
                  <div data-id="element-374">
                    <h3 className={`font-semibold ${data.experienceLevel === option.id ? 'text-brand-yellow' : 'text-white'}`} data-id="element-375">
                      {option.label}
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-376">{option.desc}</p>
                  </div>
                  {data.experienceLevel === option.id && <CheckCircleIcon className="ml-auto w-6 h-6 text-brand-yellow" data-id="element-377" />}
                </button>)}
            </div>
          </div>;
      case 2:
        return <div className="space-y-6" data-id="element-378">
            <div className="text-center mb-8" data-id="element-379">
              <h2 className="text-3xl font-bold text-white mb-2" data-id="element-380">
                Primary Goal
              </h2>
              <p className="text-gray-400" data-id="element-381">
                What do you want to achieve with LeaDe?
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-382">
              {[{
              id: 'job',
              label: 'Land a Job',
              desc: 'Get hired as a Product Designer',
              icon: BriefcaseIcon
            }, {
              id: 'freelance',
              label: 'Freelance',
              desc: 'Start or grow your business',
              icon: TrendingUpIcon
            }, {
              id: 'upskill',
              label: 'Upskill',
              desc: 'Learn new tools and methods',
              icon: SparklesIcon
            }, {
              id: 'portfolio',
              label: 'Build Portfolio',
              desc: 'Create case studies',
              icon: LayersIcon
            }].map(option => <button key={option.id} onClick={() => {
              updateData({
                learningGoal: option.id as LearningGoal
              });
              nextStep();
            }} className={`flex flex-col p-5 rounded-xl border transition-all text-left h-full ${data.learningGoal === option.id ? 'bg-brand-yellow/10 border-brand-yellow' : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'}`} data-id="element-383">
                  <option.icon className={`w-8 h-8 mb-4 ${data.learningGoal === option.id ? 'text-brand-yellow' : 'text-gray-400'}`} data-id="element-384" />
                  <h3 className={`font-semibold mb-1 ${data.learningGoal === option.id ? 'text-brand-yellow' : 'text-white'}`} data-id="element-385">
                    {option.label}
                  </h3>
                  <p className="text-sm text-gray-400" data-id="element-386">{option.desc}</p>
                </button>)}
            </div>
          </div>;
      case 3:
        return <div className="space-y-6" data-id="element-387">
            <div className="text-center mb-8" data-id="element-388">
              <h2 className="text-3xl font-bold text-white mb-2" data-id="element-389">
                Learning Style
              </h2>
              <p className="text-gray-400" data-id="element-390">How do you learn best?</p>
            </div>
            <div className="grid gap-4" data-id="element-391">
              {[{
              id: 'visual',
              label: 'Visual Learner',
              desc: 'Videos, diagrams, and examples',
              icon: PaletteIcon
            }, {
              id: 'hands-on',
              label: 'Hands-on',
              desc: 'Learning by doing projects',
              icon: LayersIcon
            }, {
              id: 'theory',
              label: 'Conceptual',
              desc: 'Reading and understanding principles',
              icon: UserIcon
            }, {
              id: 'mixed',
              label: 'Mixed',
              desc: 'A bit of everything',
              icon: SparklesIcon
            }].map(option => <button key={option.id} onClick={() => {
              updateData({
                learningStyle: option.id as LearningStyle
              });
              nextStep();
            }} className={`flex items-center p-4 rounded-xl border transition-all text-left group ${data.learningStyle === option.id ? 'bg-brand-yellow/10 border-brand-yellow' : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'}`} data-id="element-392">
                  <div className={`p-3 rounded-lg mr-4 ${data.learningStyle === option.id ? 'bg-brand-yellow text-dark-900' : 'bg-white/10 text-gray-400 group-hover:text-white'}`} data-id="element-393">
                    <option.icon className="w-6 h-6" data-id="element-394" />
                  </div>
                  <div data-id="element-395">
                    <h3 className={`font-semibold ${data.learningStyle === option.id ? 'text-brand-yellow' : 'text-white'}`} data-id="element-396">
                      {option.label}
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-397">{option.desc}</p>
                  </div>
                  {data.learningStyle === option.id && <CheckCircleIcon className="ml-auto w-6 h-6 text-brand-yellow" data-id="element-398" />}
                </button>)}
            </div>
          </div>;
      case 4:
        return <div className="space-y-6" data-id="element-399">
            <div className="text-center mb-8" data-id="element-400">
              <h2 className="text-3xl font-bold text-white mb-2" data-id="element-401">
                Time Commitment
              </h2>
              <p className="text-gray-400" data-id="element-402">
                How much time can you dedicate weekly?
              </p>
            </div>
            <div className="grid gap-4" data-id="element-403">
              {[{
              id: 'full-time',
              label: 'Full-time',
              desc: '20+ hours/week (Fast track)',
              icon: ZapIcon
            }, {
              id: 'part-time',
              label: 'Part-time',
              desc: '10-20 hours/week',
              icon: ClockIcon
            }, {
              id: 'flexible',
              label: 'Flexible',
              desc: '< 10 hours/week (Self-paced)',
              icon: UserIcon
            }].map(option => <button key={option.id} onClick={() => {
              updateData({
                timeCommitment: option.id as TimeCommitment
              });
              nextStep();
            }} className={`flex items-center p-4 rounded-xl border transition-all text-left group ${data.timeCommitment === option.id ? 'bg-brand-yellow/10 border-brand-yellow' : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'}`} data-id="element-404">
                  <div className={`p-3 rounded-lg mr-4 ${data.timeCommitment === option.id ? 'bg-brand-yellow text-dark-900' : 'bg-white/10 text-gray-400 group-hover:text-white'}`} data-id="element-405">
                    <option.icon className="w-6 h-6" data-id="element-406" />
                  </div>
                  <div data-id="element-407">
                    <h3 className={`font-semibold ${data.timeCommitment === option.id ? 'text-brand-yellow' : 'text-white'}`} data-id="element-408">
                      {option.label}
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-409">{option.desc}</p>
                  </div>
                  {data.timeCommitment === option.id && <CheckCircleIcon className="ml-auto w-6 h-6 text-brand-yellow" data-id="element-410" />}
                </button>)}
            </div>
          </div>;
      case 5:
        return <div className="space-y-6" data-id="element-411">
            <div className="text-center mb-8" data-id="element-412">
              <h2 className="text-3xl font-bold text-white mb-2" data-id="element-413">Interests</h2>
              <p className="text-gray-400" data-id="element-414">
                Select topics you're most interested in (multiple)
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-415">
              {[{
              id: 'ui',
              label: 'UI Design'
            }, {
              id: 'ux',
              label: 'UX Research'
            }, {
              id: 'systems',
              label: 'Design Systems'
            }, {
              id: 'motion',
              label: 'Motion'
            }, {
              id: 'strategy',
              label: 'Product Strategy'
            }, {
              id: 'prototyping',
              label: 'Prototyping'
            }].map(option => <button key={option.id} onClick={() => toggleInterest(option.id as Interest)} className={`p-4 rounded-xl border transition-all text-center ${data.interests.includes(option.id as Interest) ? 'bg-brand-yellow/10 border-brand-yellow text-brand-yellow font-semibold' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'}`} data-id="element-416">
                  {option.label}
                </button>)}
            </div>
          </div>;
      case 6:
        return <div className="space-y-8" data-id="element-417">
            <div className="text-center" data-id="element-418">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-yellow/20 mb-6" data-id="element-419">
                <SparklesIcon className="w-8 h-8 text-brand-yellow" data-id="element-420" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2" data-id="element-421">
                Your Personalized Plan
              </h2>
              <p className="text-gray-400" data-id="element-422">
                Based on your profile, here's what we recommend:
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden" data-id="element-423">
              <div className="absolute top-0 right-0 p-3 bg-brand-yellow text-dark-900 text-xs font-bold rounded-bl-xl" data-id="element-424">
                {recommendation.match}% MATCH
              </div>

              <h3 className="text-xl font-bold text-white mb-1" data-id="element-425">
                {recommendation.track}
              </h3>
              <p className="text-brand-yellow text-sm mb-6" data-id="element-426">
                {recommendation.duration} • {recommendation.focus}
              </p>

              <div className="space-y-3 mb-6" data-id="element-427">
                <div className="flex items-center text-sm text-gray-300" data-id="element-428">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-3" data-id="element-429" />
                  <span data-id="element-430">Tailored for {data.experienceLevel} level</span>
                </div>
                <div className="flex items-center text-sm text-gray-300" data-id="element-431">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-3" data-id="element-432" />
                  <span data-id="element-433">Focus on {data.learningGoal} goals</span>
                </div>
                <div className="flex items-center text-sm text-gray-300" data-id="element-434">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 mr-3" data-id="element-435" />
                  <span data-id="element-436">Fits your {data.timeCommitment} schedule</span>
                </div>
              </div>

              <div className="p-4 bg-dark-900/50 rounded-xl border border-white/5 mb-6" data-id="element-437">
                <p className="text-sm text-gray-400 italic" data-id="element-438">
                  "This track is perfect for {data.name || 'you'}. We'll focus
                  on{' '}
                  {data.interests.slice(0, 2).join(' and ') || 'fundamentals'}{' '}
                  to help you achieve your goal of{' '}
                  {data.learningGoal === 'job' ? 'landing a job' : 'growing your skills'}
                  ."
                </p>
              </div>

              <button onClick={onClose} className="w-full py-4 bg-brand-yellow text-dark-900 font-bold rounded-xl hover:bg-brand-yellow-dark transition-colors flex items-center justify-center gap-2" data-id="element-439">
                Start Your Journey
                <ChevronRightIcon className="w-5 h-5" data-id="element-440" />
              </button>
            </div>

            <p className="text-center text-xs text-gray-500" data-id="element-441">
              No credit card required for trial access.
            </p>
          </div>;
      default:
        return null;
    }
  };
  return <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4" data-id="element-442">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="absolute inset-0 bg-dark-900/95 backdrop-blur-sm" onClick={onClose} data-id="element-443" />

      <motion.div initial={{
      scale: 0.95,
      opacity: 0,
      y: 20
    }} animate={{
      scale: 1,
      opacity: 1,
      y: 0
    }} exit={{
      scale: 0.95,
      opacity: 0,
      y: 20
    }} className="relative w-full max-w-lg bg-dark-800 border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-444">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-dark-800 z-10" data-id="element-445">
          <div className="flex items-center gap-2" data-id="element-446">
            <div className="w-8 h-8 rounded-full bg-brand-yellow/10 flex items-center justify-center text-brand-yellow font-bold text-sm" data-id="element-447">
              {currentStep + 1}
            </div>
            <span className="text-sm text-gray-400" data-id="element-448">of {totalSteps}</span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors" data-id="element-449">
            <XIcon className="w-6 h-6" data-id="element-450" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-dark-700 w-full" data-id="element-451">
          <motion.div className="h-full bg-brand-yellow" initial={{
          width: 0
        }} animate={{
          width: `${progress}%`
        }} transition={{
          duration: 0.3
        }} data-id="element-452" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar" data-id="element-453">
          <AnimatePresence mode="wait" custom={1} data-id="element-454">
            <motion.div key={currentStep} custom={1} variants={variants} initial="enter" animate="center" exit="exit" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-455">
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer Navigation */}
        {currentStep < totalSteps - 1 && <div className="px-6 py-4 border-t border-white/5 bg-dark-800 flex justify-between items-center z-10" data-id="element-456">
            <button onClick={prevStep} disabled={currentStep === 0} className={`flex items-center gap-1 text-sm font-medium transition-colors ${currentStep === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-gray-400 hover:text-white'}`} data-id="element-457">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-458" />
              Back
            </button>
            <button onClick={nextStep} disabled={currentStep === 0 && !data.name} // Disable next on first step if empty
        className="flex items-center gap-2 px-6 py-2.5 bg-white text-dark-900 font-semibold rounded-full hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-459">
              Next
              <ChevronRightIcon className="w-4 h-4" data-id="element-460" />
            </button>
          </div>}
      </motion.div>
    </div>;
}